import React, { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Check,
} from "lucide-react";

/**
 * Kunden-Verschiebungs-Komponente (Bootstrap)
 * Ermöglicht Kunden, ihren Termin zu verschieben
 */
const CustomerReschedule = () => {
  // Lese Props aus data-Attributen
  const rootElement = document.getElementById("customer-reschedule-root");
  const appointmentId = rootElement?.dataset.appointmentId;
  const userSID = rootElement?.dataset.userSid;

  const [appointment, setAppointment] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Lade Termin-Daten
  useEffect(() => {
    loadAppointment();
  }, [appointmentId, userSID]);

  // Lade Slots wenn Termin geladen wurde
  useEffect(() => {
    if (appointment) {
      loadAvailableSlots();
    }
  }, [appointment, currentMonth]);

  const loadAppointment = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/index.php/customer/getAppointmentInfo?aID=${appointmentId}&uSID=${userSID}`
      );
      const data = await response.json();

      if (data.success) {
        setAppointment(data.appointment);
      } else {
        setError(data.error || "Termin konnte nicht geladen werden");
      }
    } catch (error) {
      console.error("Fehler:", error);
      setError("Ein Fehler ist aufgetreten");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableSlots = async () => {
    if (!appointment) return;

    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();

      const startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month + 1, 0).getDate();
      const endDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        lastDay
      ).padStart(2, "0")}`;

      const response = await fetch(
        `/index.php/customer/getPublicAvailableSlots?` +
          new URLSearchParams({
            calendar_id: appointment.appointment_calendar_id,
            type_id: appointment.appointment_type_id,
            userSID: userSID,
            start_date: startDate,
            end_date: endDate,
          })
      );

      const data = await response.json();

      if (data.success) {
        setAvailableSlots(data.slots || {});
      }
    } catch (error) {
      console.error("Fehler beim Laden der Slots:", error);
    }
  };

  const handleReschedule = async () => {
    if (!selectedDate || !selectedSlot) {
      alert("Bitte wählen Sie einen neuen Termin aus");
      return;
    }

    setRescheduling(true);
    setError(null);

    try {
      const formData = new URLSearchParams();
      formData.append("aID", appointmentId);
      formData.append("uSID", userSID);
      formData.append("new_date", selectedDate);
      formData.append("new_time", selectedSlot);

      const response = await fetch(
        "/index.php/customer/rescheduleAppointment",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          window.location.href = `/index.php/customer/appointment_rescheduled?aID=${appointmentId}&uSID=${userSID}`;
        }, 3000);
      } else {
        setError(data.error || "Termin konnte nicht verschoben werden");
      }
    } catch (error) {
      console.error("Fehler:", error);
      setError("Ein Fehler ist aufgetreten");
    } finally {
      setRescheduling(false);
    }
  };

  const renderCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    const dayNames = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

    // Leere Tage am Anfang (Montag als erster Tag)
    const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;
    for (let i = 0; i < adjustedFirstDay; i++) {
      days.push(<div key={`empty-${i}`} />);
    }

    // Tage des Monats
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;
      const hasSlots =
        availableSlots[dateStr] && availableSlots[dateStr].length > 0;
      const isSelected = selectedDate === dateStr;
      const dateObj = new Date(year, month, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPast = dateObj < today;

      let btnClass = "btn d-flex align-items-center justify-content-center";
      let btnStyle = {
        aspectRatio: "1 / 1",
        fontSize: "0.875rem",
        padding: "0px",
      };

      if (isPast || !hasSlots) {
        btnClass += " btn-outline-secondary";
        btnStyle = {
          ...btnStyle,
          borderColor: "#dee2e6",
          color: "#6c757d",
          opacity: 0.4,
          cursor: "not-allowed",
          fontWeight: 400,
        };
      } else if (isSelected) {
        btnStyle = {
          ...btnStyle,
          backgroundColor: "#3b82f6",
          borderColor: "#3b82f6",
          color: "#ffffff",
          opacity: 1,
          cursor: "pointer",
          fontWeight: 600,
        };
      } else if (hasSlots) {
        btnClass += " btn-outline-secondary";
        btnStyle = {
          ...btnStyle,
          borderColor: "#dee2e6",
          color: "#212529",
          opacity: 1,
          cursor: "pointer",
          fontWeight: 400,
        };
      }

      days.push(
        <button
          key={day}
          onClick={() => {
            if (!isPast && hasSlots) {
              setSelectedDate(dateStr);
              setSelectedSlot(null);
            }
          }}
          disabled={isPast || !hasSlots}
          className={btnClass}
          style={btnStyle}
        >
          {day}
        </button>
      );
    }

    return (
      <>
        <div
          className="d-grid mb-2"
          style={{
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "4px",
          }}
        >
          {dayNames.map((name) => (
            <div key={name} className="text-center py-2">
              <small className="text-muted fw-semibold">{name}</small>
            </div>
          ))}
        </div>

        <div
          className="d-grid"
          style={{
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: "4px",
          }}
        >
          {days}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p className="text-muted">Lade Termin...</p>
        </div>
      </div>
    );
  }

  if (error && !appointment) {
    return (
      <div className="container py-5">
        <div
          className="alert alert-danger d-flex align-items-center"
          role="alert"
        >
          <AlertCircle size={24} className="me-2" />
          <div>
            <h5 className="alert-heading mb-1">Fehler</h5>
            <p className="mb-0">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="container py-5">
        <div className="alert alert-success text-center" role="alert">
          <Check size={48} className="mb-3 text-success" />
          <h4 className="alert-heading">Termin erfolgreich verschoben!</h4>
          <p>Sie erhalten eine Bestätigungsmail mit den neuen Termindaten.</p>
          <hr />
          <p className="mb-0 small text-muted">
            Sie werden automatisch weitergeleitet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: "900px" }}>
      {/* Header */}
      <div className="mb-5 text-center">
        <h1 className="display-5 fw-bold mb-2">Termin verschieben</h1>
        <p className="text-muted">
          Wählen Sie einen neuen Termin für Ihre Buchung
        </p>
      </div>

      {/* Aktueller Termin */}
      <div
        className="card shadow-sm mb-4"
        style={{ backgroundColor: "#e7f3ff" }}
      >
        <div className="card-body">
          <h5 className="card-title mb-3">Aktueller Termin</h5>
          <div className="row g-3">
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Calendar size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Termintyp</small>
                  <strong>{appointment?.type_name}</strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Calendar size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Datum</small>
                  <strong>
                    {appointment?.appointment_date &&
                      new Date(
                        ...appointment.appointment_date
                          .split("-")
                          .map((n, i) => (i === 1 ? n - 1 : n))
                      ).toLocaleDateString("de-DE")}
                  </strong>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Clock size={18} className="me-2 text-primary" />
                <div>
                  <small className="text-muted d-block">Uhrzeit</small>
                  <strong>
                    {appointment?.start_time} - {appointment?.end_time} Uhr
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Neuen Termin wählen */}
      <div className="mb-4">
        <h4 className="mb-4">Neuen Termin wählen</h4>

        <div className="row g-4">
          {/* Kalender - Links */}
          <div className="col-lg-7">
            <div className="card">
              <div className="card-body">
                {/* Monats-Navigation */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <button
                    onClick={() =>
                      setCurrentMonth(
                        new Date(
                          currentMonth.getFullYear(),
                          currentMonth.getMonth() - 1
                        )
                      )
                    }
                    className="btn btn-sm btn-outline-secondary"
                  >
                    ←
                  </button>
                  <h4 className="h6 fw-semibold mb-0">
                    {currentMonth.toLocaleDateString("de-DE", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h4>
                  <button
                    onClick={() =>
                      setCurrentMonth(
                        new Date(
                          currentMonth.getFullYear(),
                          currentMonth.getMonth() + 1
                        )
                      )
                    }
                    className="btn btn-sm btn-outline-secondary"
                  >
                    →
                  </button>
                </div>

                {/* Kalender */}
                {renderCalendar()}
              </div>
            </div>
          </div>

          {/* Zeitslots - Rechts */}
          <div className="col-lg-5">
            {selectedDate && availableSlots[selectedDate] ? (
              <div className="card">
                <div className="card-body">
                  <h4 className="h6 fw-semibold mb-3">
                    Verfügbare Zeiten für{" "}
                    {(() => {
                      const [year, month, day] = selectedDate
                        .split("-")
                        .map(Number);
                      return new Date(year, month - 1, day).toLocaleDateString(
                        "de-DE",
                        {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        }
                      );
                    })()}
                  </h4>
                  <div className="d-grid gap-2">
                    {availableSlots[selectedDate].map((slot) => {
                      const slotTime =
                        typeof slot === "string" ? slot : slot.time;
                      return (
                        <button
                          key={slotTime}
                          onClick={() => setSelectedSlot(slotTime)}
                          className={`btn text-start ${
                            selectedSlot === slotTime
                              ? "btn-primary"
                              : "btn-outline-secondary"
                          }`}
                        >
                          <Clock size={16} className="me-2" />
                          {slotTime} Uhr
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="card bg-light">
                <div className="card-body text-center py-5 text-muted">
                  Bitte wählen Sie ein Datum aus dem Kalender
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fehler */}
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center mb-4"
          role="alert"
        >
          <AlertCircle size={20} className="me-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Buttons */}
      <div className="d-flex gap-2 justify-content-center mt-5">
        <button
          onClick={handleReschedule}
          disabled={!selectedDate || !selectedSlot || rescheduling}
          className="btn btn-outline-primary btn-lg px-5"
        >
          {rescheduling ? "Wird verschoben..." : "Termin verschieben"}
        </button>
        <a
          href={appointment?.calendar_uuid
            ? `/index.php/customer/appointment?calendar_id=${appointment.calendar_uuid}&userSID=${userSID}`
            : "/"
          }
          className="btn btn-outline-secondary btn-lg px-4"
        >
          Abbrechen
        </a>
      </div>
    </div>
  );
};

export default CustomerReschedule;
